// src/pages/Billing.js (CLIENT/SUPERADMIN – wired to Billing.css hero + poweredby)
// ✅ Loader stays up until READY shell is actually visible (GenesisHub pattern)

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import "./Dashboard.css"; // for core-tile / cortex-beta-tag base styles
import "./Billing.css";

import CphereHeaderLogo from "../components/CphereHeaderLogo";
import Loading from "../components/Loading";

import cphereHeaderLogo from "../assets/Cphere Logo New.png";
import backgroundImage from "../assets/background.jpg";

import billingPortalImage from "../assets/billingportal.jpg";
import appleImage from "../assets/apple.jpg";
import googleImage from "../assets/google.jpg";
import xrpImage from "../assets/xrp.jpg";
import xlmImage from "../assets/xlm.jpg";
import hbarImage from "../assets/hbar.png";
import algoImage from "../assets/algo.png";
import usdcImage from "../assets/usdc.jpg";

import { QRCodeSVG } from "qrcode.react";

const ZOHO_CLIENT_PORTAL_URL = "https://invoice.zohosecure.com/portal/cphere";
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
const safeBase = (base) => String(base || "").replace(/\/+$/, "");
const STATIC_XRP_ADDRESS = "rBgGSb9ureSZHvn7LQTA7UqoaE7WTLJhFc";

// Map crypto symbols to their governed compute funding rail and brand mark.
//
// `centerLogo` is artwork ONLY — the network's own mark, drawn in the middle of
// the QR. It never carries an address. The payload is still derived from the
// resolved funding address, so the displayed address, the Copy action, and the
// QR payload cannot disagree. Pre-rendered QR images are deliberately not used:
// they embed a destination that silently outlives a configuration change.
const CRYPTO_CONFIG = {
  XRP: {
    label: "XRP",
    railId: "xrp",
    fallbackAddress: STATIC_XRP_ADDRESS,
    centerLogo: xrpImage,
  },
  XLM: {
    label: "XLM",
    railId: "xlm",
    centerLogo: xlmImage,
  },
  HBAR: {
    label: "HBAR",
    railId: "hbar",
  },
  ALGO: {
    label: "ALGO",
    railId: "algo",
  },
  USDC: {
    label: "USDC",
    railId: "tron_usdc",
    centerLogo: usdcImage,
  },
};

// QR geometry. The centred mark occludes modules, so the code is generated at
// error-correction level H (~30% recoverable) with a full 4-module quiet zone.
// The logo stays a small fraction of the code so the excavated area remains
// well inside what level H can reconstruct.
const QR_SIZE = 220;
const QR_CENTER_LOGO_SIZE = 48;
const QR_QUIET_ZONE_MODULES = 4;

const CRYPTO_NETWORK_FALLBACKS = {
  HBAR: "Hedera",
  ALGO: "Algorand",
  USDC: "TRON",
};

const CRYPTO_BILLING_CARDS = [
  {
    symbol: "XRP",
    title: "XRP",
    description: "Ripple",
    image: xrpImage,
  },
  {
    symbol: "XLM",
    title: "XLM",
    description: "Stellar",
    image: xlmImage,
  },
  {
    symbol: "HBAR",
    title: "HBAR",
    description: "Hedera",
    image: hbarImage,
  },
  {
    symbol: "ALGO",
    title: "ALGO",
    description: "Algorand",
    image: algoImage,
  },
  {
    symbol: "USDC",
    title: "USDC",
    description: "TRON Network",
    image: usdcImage,
  },
];

function humanizeStatus(value, fallback = "Unavailable") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function looksLikePlaceholderAddress(value) {
  const text = String(value || "").trim();
  return !text || /<|>|\byour\b|\bplaceholder\b|\baddress\b/i.test(text);
}

function isValidTronAddress(value) {
  return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(String(value || "").trim());
}

function isValidStellarPublicKey(value) {
  return /^G[A-Z2-7]{55}$/.test(String(value || "").trim());
}

function isValidXrpAddress(value) {
  return /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(String(value || "").trim());
}

// Plain network name for prose. railNetworkLabel() is the badge form.
function railNetworkName(rail) {
  const network = String(rail?.network || "").trim().toLowerCase();
  if (network === "tron") return "TRON";
  if (network === "stellar") return "Stellar";
  if (network === "hedera") return "Hedera";
  if (network === "algorand") return "Algorand";
  if (network === "xrpl") return "the XRP Ledger";
  return network.toUpperCase();
}

function findRailForCrypto(computeRails = [], symbol) {
  const railId = CRYPTO_CONFIG[symbol]?.railId;
  if (!railId) return null;
  return (Array.isArray(computeRails) ? computeRails : []).find((rail) => rail?.railId === railId) || null;
}

// The backend projection has already validated the destination against its own
// network. This is a second, independent gate: a value that fails here is never
// displayed, copied, or encoded into a QR.
function railFundingAddress(rail, symbol = "") {
  const address = String(rail?.fundingAddress || "").trim();
  if (!address || looksLikePlaceholderAddress(address)) return "";
  if (rail?.displayState !== "available") return "";

  const network = String(rail?.network || "").trim().toLowerCase();
  if (network === "tron") return isValidTronAddress(address) ? address : "";
  if (network === "stellar") return isValidStellarPublicKey(address) ? address : "";
  if (network === "xrpl") return isValidXrpAddress(address) ? address : "";
  return "";
}

function fundingNoticeForCrypto(symbol, rail, hasAddress) {
  if (!hasAddress) return "";
  if (rail?.activationPending) {
    return "This Stellar address may require first funding before it can receive compute payments.";
  }
  if (rail?.assetRestricted) {
    return `Use only ${rail?.assetCode || symbol} on ${railNetworkName(rail)} for this compute funding option.`;
  }
  return "";
}

function railNetworkLabel(rail, fallback = "") {
  const network = String(rail?.network || fallback || "").trim();
  const tokenStandard = String(rail?.tokenStandard || "").trim();
  if (!network && !tokenStandard) return "";
  if (network.toLowerCase() === "hedera") return "Hedera";
  if (network.toLowerCase() === "algorand") return "Algorand";
  const normalizedNetwork = network.toLowerCase() === "tron" ? "TRON" : network.toUpperCase();
  if (tokenStandard) return `${normalizedNetwork} / ${tokenStandard.replace(/^TRC20$/i, "TRC-20")}`;
  return normalizedNetwork;
}

function buildCryptoFundingConfig(symbol, computeRails = [], loadFailed = false) {
  const base = CRYPTO_CONFIG[symbol];
  if (!base) return null;
  const rail = findRailForCrypto(computeRails, symbol);
  const railAddress = railFundingAddress(rail, symbol);
  const address = railAddress || (symbol === "XRP" ? base.fallbackAddress : "");
  const readiness = humanizeStatus(rail?.displayState, "Unavailable");

  return {
    ...base,
    rail,
    // One canonical destination. The rendered address, the Copy action, and the
    // QR payload all read this single value — they cannot drift apart.
    address,
    networkLabel: railNetworkLabel(rail, CRYPTO_NETWORK_FALLBACKS[symbol] || symbol),
    fundingNotice: fundingNoticeForCrypto(symbol, rail, Boolean(address)),
    addressUnavailableLabel: "Funding address unavailable.",
    futureSupported: rail?.displayState === "future_supported" || ["HBAR", "ALGO"].includes(symbol),
    // A failed load is not the same as a governed rail having no destination.
    loadFailed: loadFailed && !address,
    readiness,
  };
}

function cryptoModalSubtitle(config) {
  if (config?.address) return "Scan QR code or use the wallet address below.";
  if (config?.futureSupported) {
    return "Reserved for future support. Contact your Cphere team before funding.";
  }
  if (config?.loadFailed) {
    return "Funding details could not be loaded. Retry, or contact your Cphere team before funding.";
  }
  return "Funding address unavailable for this payment option.";
}

function BillingCardIcon({ src, alt }) {
  const [imageFailed, setImageFailed] = useState(false);
  const fallback = String(alt || "?").trim().split(/\s+/)[0] || "?";

  if (!src || imageFailed) {
    return <div className="billing-card-icon-fallback">{fallback}</div>;
  }

  return (
    <img
      src={src}
      alt={alt}
      className="billing-card-icon"
      onError={() => setImageFailed(true)}
    />
  );
}

// The funding QR. `address` is the single source of truth for the payload; the
// brand mark is decoration layered on top of it.
//
// The mark is only applied once it has actually loaded. Excavation clears the
// modules underneath, so applying it to artwork that never arrives would punch a
// blank hole in the code for no benefit; until then — and permanently, if the
// asset fails — this renders the plain generated QR.
function BrandedFundingQr({ address, label, centerLogo }) {
  const [logoReady, setLogoReady] = useState(false);

  useEffect(() => {
    setLogoReady(false);
    if (!centerLogo) return undefined;

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setLogoReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setLogoReady(false);
    };
    img.src = centerLogo;

    return () => {
      cancelled = true;
    };
  }, [centerLogo]);

  return (
    <QRCodeSVG
      value={address}
      title={`${label} funding address QR code`}
      className="billing-crypto-qr-image"
      size={QR_SIZE}
      level="H"
      marginSize={QR_QUIET_ZONE_MODULES}
      bgColor="#ffffff"
      fgColor="#000000"
      imageSettings={
        logoReady
          ? {
              src: centerLogo,
              height: QR_CENTER_LOGO_SIZE,
              width: QR_CENTER_LOGO_SIZE,
              excavate: true,
            }
          : undefined
      }
    />
  );
}

/* ✅ module-level cache so returning is smooth */
let CLIENT_BILLING_ASSETS_WARM = false;

function preloadImage(url, timeoutMs = 4500) {
  return new Promise((resolve) => {
    if (!url) return resolve();

    const img = new Image();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    const timer = setTimeout(finish, timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      if (typeof img.decode === "function") {
        img.decode().then(finish).catch(finish);
      } else {
        finish();
      }
    };

    img.onerror = () => {
      clearTimeout(timer);
      finish();
    };

    img.src = url;
  });
}

const Billing = () => {
  const navigate = useNavigate();

  const [cryptoModalOpen, setCryptoModalOpen] = useState(false);
  const [activeCrypto, setActiveCrypto] = useState(null);
  const [copyLabel, setCopyLabel] = useState("Copy");
  const [walletMethods, setWalletMethods] = useState([]);
  const [computeRails, setComputeRails] = useState([]);
  const [railsLoadFailed, setRailsLoadFailed] = useState(false);

  // ✅ asset-ready gate
  const [assetsReady, setAssetsReady] = useState(CLIENT_BILLING_ASSETS_WARM);
  const [fadeIn, setFadeIn] = useState(false);
  const [loaderHidden, setLoaderHidden] = useState(false);

  const hideLoaderFallbackRef = useRef(null);

  // Ensure route-specific CSS (body[data-route="/billing"]) is active
  useEffect(() => {
    document.body.setAttribute("data-route", "/billing");
    return () => {
      document.body.removeAttribute("data-route");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadComputeRails = async () => {
      try {
        if (!API_BASE_URL) {
          setRailsLoadFailed(true);
          return;
        }
        const token = localStorage.getItem("authToken");
        if (!token) {
          setRailsLoadFailed(true);
          return;
        }

        const res = await fetch(`${safeBase(API_BASE_URL)}/api/billing/funding-rails`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (cancelled) return;

        // A non-OK response previously fell through silently, which made an
        // authorization failure look identical to a rail with no destination.
        if (!res.ok) {
          console.error(`[Billing] Funding rails request failed (${res.status}).`);
          setComputeRails([]);
          setRailsLoadFailed(true);
          return;
        }

        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setComputeRails(Array.isArray(data?.rails) ? data.rails : []);
        setRailsLoadFailed(!Array.isArray(data?.rails));
      } catch (error) {
        if (cancelled) return;
        console.error("[Billing] Funding rails request failed:", error);
        setComputeRails([]);
        setRailsLoadFailed(true);
      }
    };

    loadComputeRails();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadWalletMethods = async () => {
      try {
        if (!API_BASE_URL) return;
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const res = await fetch(`${safeBase(API_BASE_URL)}/api/billing/wallet-options`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setWalletMethods(Array.isArray(data.walletMethods) ? data.walletMethods : []);
      } catch {
        if (!cancelled) setWalletMethods([]);
      }
    };

    loadWalletMethods();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (CLIENT_BILLING_ASSETS_WARM) {
      setAssetsReady(true);
      return;
    }

    const urls = [
      backgroundImage,
      cphereHeaderLogo,
      billingPortalImage,
      appleImage,
      googleImage,
      xrpImage,
      xlmImage,
      hbarImage,
      algoImage,
      usdcImage,
      // QR codes are rendered as SVG from the resolved address, so there is no
      // QR artwork left to preload.
    ];

    (async () => {
      try {
        await Promise.all(urls.map((u) => preloadImage(u)));
      } finally {
        if (cancelled) return;
        CLIENT_BILLING_ASSETS_WARM = true;
        setAssetsReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!assetsReady) {
      setFadeIn(false);
      setLoaderHidden(false);
      if (hideLoaderFallbackRef.current) {
        clearTimeout(hideLoaderFallbackRef.current);
        hideLoaderFallbackRef.current = null;
      }
      return;
    }

    // ✅ fade-in after mount so CSS transition runs
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setFadeIn(true));
    });

    // ✅ fallback in case transitionend doesn't fire
    hideLoaderFallbackRef.current = setTimeout(() => {
      setLoaderHidden(true);
      hideLoaderFallbackRef.current = null;
    }, 900);

    return () => cancelAnimationFrame(raf);
  }, [assetsReady]);

  const showLoader = !assetsReady || !loaderHidden;

  const handleReadyShellTransitionEnd = useCallback(
    (e) => {
      if (e?.propertyName !== "opacity") return;
      if (!fadeIn) return;

      setLoaderHidden(true);

      if (hideLoaderFallbackRef.current) {
        clearTimeout(hideLoaderFallbackRef.current);
        hideLoaderFallbackRef.current = null;
      }
    },
    [fadeIn]
  );

  const handleLogoClick = () => navigate("/dashboard");

  const handleBillingPortalClick = () => {
    window.open(ZOHO_CLIENT_PORTAL_URL, "_blank", "noopener,noreferrer");
  };

  const handleCryptoClick = (symbol) => {
    setActiveCrypto(symbol);
    setCopyLabel("Copy");
    setCryptoModalOpen(true);
  };

  const handleCloseModal = () => {
    setCryptoModalOpen(false);
    setActiveCrypto(null);
  };

  const currentConfig = useMemo(
    () => (activeCrypto ? buildCryptoFundingConfig(activeCrypto, computeRails, railsLoadFailed) : null),
    [activeCrypto, computeRails, railsLoadFailed]
  );

  const handleCopyAddress = async () => {
    const cfg = currentConfig;
    if (!cfg?.address) return;

    try {
      await navigator.clipboard.writeText(cfg.address);
      setCopyLabel("Copied!");
      setTimeout(() => setCopyLabel("Copy"), 1500);
    } catch (e) {
      console.error("Clipboard copy failed:", e);
      setCopyLabel("Copy");
    }
  };

  // Helpers so tiles are clickable + keyboard-accessible
  const makeTileHandlers = (handler) => ({
    onClick: handler,
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handler();
      }
    },
    role: "button",
    tabIndex: 0,
  });

  const walletMethodMap = useMemo(
    () =>
      new Map(
        (Array.isArray(walletMethods) ? walletMethods : []).map((method) => [method.method, method])
      ),
    [walletMethods]
  );

  const walletCards = [
    {
      key: "apple_pay",
      title: "APPLE PAY",
      description: "Express Checkout",
      image: appleImage,
    },
    {
      key: "google_pay",
      title: "GOOGLE PAY",
      description: "Express Checkout",
      image: googleImage,
    },
  ];

  return (
    <div className="billing-dashboard">
      {/* ✅ Loader shell stays visible until ready shell fade-in is DONE */}
      {showLoader && (
        <div className="billing-loading-shell">
          <Loading />
        </div>
      )}

      {/* ✅ Ready shell starts hidden (opacity:0) until fadeIn flips */}
      <div
        className={`billing-ready-shell ${fadeIn ? "billing-fade-in" : ""}`}
        aria-hidden={!assetsReady}
        onTransitionEnd={handleReadyShellTransitionEnd}
      >
        <div className="billing-content billing-page-content">
          {/* Powered by Logo – wired to Billing.css, matches ProfileHub specs */}
          <CphereHeaderLogo
            alt="Powered by Logo"
            className="billing-poweredby"
            onClick={handleLogoClick}
          />

          {/* Hero – wired to Billing.css, visually matching ProfileHub hero */}
          <div className="billing-hero">
            <div className="billing-hero-pill">
              <span className="billing-hero-label cphere-hero-pill-wordmark">BILLING</span>
              <span className="billing-hero-tag cphere-hero-pill-tag">PAYMENTS</span>
            </div>
            <div className="billing-hero-shimmer" />
          </div>

          {/* Tile layout – using core-tile for consistent plate style */}
          <div className="billing-tiles-container">
            {/* Zoho Billing Portal */}
            <div
              className="billing-tile core-tile"
              {...makeTileHandlers(handleBillingPortalClick)}
            >
              <div className="billing-tile-inner">
                <div className="billing-top-block">
                  <div className="billing-card-icon-wrapper">
                    <img
                      src={billingPortalImage}
                      alt="Billing Portal"
                      className="billing-card-icon"
                    />
                  </div>
                  <div className="billing-card-title">BILLING PORTAL</div>
                  <div className="billing-card-description">MANAGE INVOICES</div>
                </div>

                <button
                  className="billing-card-button integration-button integration-button--connect"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBillingPortalClick();
                  }}
                >
                  <span className="integration-button-label">OPEN PORTAL</span>
                </button>
              </div>
            </div>

            {CRYPTO_BILLING_CARDS.map((card) => (
              <div
                key={card.symbol}
                className="billing-tile core-tile"
                {...makeTileHandlers(() => handleCryptoClick(card.symbol))}
              >
                <span className="cortex-beta-tag">CRYPTO</span>
                <div className="billing-tile-inner">
                  <div className="billing-top-block">
                    <div className="billing-card-icon-wrapper">
                      <BillingCardIcon src={card.image} alt={card.title} />
                    </div>
                    <div className="billing-card-title">{card.title}</div>
                    <div className="billing-card-description">{card.description}</div>
                  </div>

                  <button
                    className="billing-card-button integration-button integration-button--connect"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCryptoClick(card.symbol);
                    }}
                  >
                    <span className="integration-button-label">COMPUTE WITH {card.title}</span>
                  </button>
                </div>
              </div>
            ))}

            {walletCards.map((card) => {
              const method = walletMethodMap.get(card.key) || null;
              const statusLabel = "COMING SOON";
              const walletConfigured = method?.ready || method?.enabled ? "true" : "false";

              return (
                <div
                  key={card.key}
                  className="billing-tile core-tile billing-wallet-tile"
                  aria-disabled="true"
                  data-wallet-configured={walletConfigured}
                >
                  <span className="cortex-beta-tag">WALLET</span>
                  <div className="billing-tile-inner">
                    <div className="billing-top-block">
                      <div className="billing-card-icon-wrapper">
                        <BillingCardIcon src={card.image} alt={card.title} />
                      </div>
                      <div className="billing-card-title">{card.title}</div>
                      <div className="billing-card-description">{card.description}</div>
                    </div>

                    <button type="button" className="billing-card-button integration-button integration-button--connect" disabled>
                      <span className="integration-button-label">{statusLabel}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Crypto QR Modal */}
        {cryptoModalOpen && currentConfig && (
          <div
            className="billing-crypto-modal-backdrop"
            onClick={handleCloseModal}
          >
            <div
              className="billing-crypto-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="billing-crypto-modal-close"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                &times;
              </button>

              <h2 className="billing-crypto-modal-title">
                Fund compute with {currentConfig.label}
              </h2>
              <p className="billing-crypto-modal-subtitle">
                {cryptoModalSubtitle(currentConfig)}
              </p>
              {currentConfig.networkLabel && (
                <div className="billing-crypto-network-label">
                  Network: {currentConfig.networkLabel}
                </div>
              )}

              {currentConfig.address ? (
                <div
                  className="billing-crypto-qr-wrapper"
                  data-qr-payload={currentConfig.address}
                >
                  <BrandedFundingQr
                    address={currentConfig.address}
                    label={currentConfig.label}
                    centerLogo={currentConfig.centerLogo}
                  />
                </div>
              ) : (
                <div className="billing-crypto-qr-wrapper billing-crypto-qr-unavailable">
                  {currentConfig.loadFailed
                    ? "Funding details could not be loaded."
                    : "Funding address unavailable."}
                </div>
              )}

              <div className="billing-crypto-wallet-row">
                <span className="billing-crypto-wallet-address">
                  {currentConfig.address || currentConfig.addressUnavailableLabel}
                </span>
                <button
                  className="billing-crypto-copy-button"
                  onClick={handleCopyAddress}
                  disabled={!currentConfig.address}
                >
                  {copyLabel}
                </button>
              </div>

              {currentConfig.fundingNotice && (
                <p className="billing-crypto-footer-note">
                  {currentConfig.fundingNotice}
                </p>
              )}

              <p className="billing-crypto-footer-note">
                Confirm network details with your Cphere team before funding.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;
